import React, { useMemo, useRef, useState } from 'react';
import { Hash, ChevronLeft, ChevronRight, Tv, CalendarRange, Star, Filter } from 'lucide-react';
import { useAppStore } from '@/store/app-store';
import clsx from 'clsx';

export const HorizontalCategoryBar: React.FC = () => {
  const {
    currentView,
    liveCategories,
    vodCategories,
    seriesCategories,
    selectedCategory,
    setSelectedCategory,
    setCurrentView,
    liveStreams,
    vodStreams,
    series,
    theme,
  } = useAppStore();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [categorySearch, setCategorySearch] = useState('');

  // Determine which categories to show based on current view
  const categories =
    currentView === 'live'
      ? liveCategories
      : currentView === 'vod'
      ? vodCategories
      : currentView === 'series'
      ? seriesCategories
      : [];

  const streamsForView = currentView === 'live' ? liveStreams : currentView === 'vod' ? vodStreams : series;

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    streamsForView.forEach((s: any) => {
      if (s.category_id) {
        counts[s.category_id] = (counts[s.category_id] || 0) + 1;
      }
    });
    return counts;
  }, [streamsForView]);

  const filteredCategories = useMemo(() => {
    const term = categorySearch.trim().toLowerCase();
    const list = categories.slice().sort((a, b) => a.category_name.localeCompare(b.category_name));
    if (!term) return list;
    return list.filter((c) => c.category_name.toLowerCase().includes(term));
  }, [categories, categorySearch]);

  // Only show for content views
  if (currentView === 'favorites' || currentView === 'settings' || currentView === 'epg') {
    return null;
  }

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = scrollContainerRef.current.scrollLeft +
        (direction === 'right' ? scrollAmount : -scrollAmount);

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  // Hide the entire bar if categories are empty
  if (categories.length === 0) {
    return null;
  }

  return (
    <div
      className={clsx(
        'border-b backdrop-blur z-30',
        theme === 'dark' ? 'border-white/5 bg-dark-900/95' : 'border-gray-200 bg-white/95'
      )}
    >
      <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={clsx(
            'text-xs uppercase tracking-wide font-semibold px-3 py-1 rounded-full',
            theme === 'dark' ? 'bg-white/5 text-gray-200 border border-white/5' : 'bg-gray-200 text-gray-700'
          )}>
            Guide Navigator
          </span>
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-400">
            <span>{filteredCategories.length} categories</span>
            <span className="w-1 h-1 rounded-full bg-gray-500" />
            <span>{streamsForView.length} {currentView === 'live' ? 'channels' : 'titles'}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className={clsx(
            'hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg border',
            theme === 'dark' ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200'
          )}>
            <Filter size={16} className="text-gray-500" />
            <input
              type="text"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder="Filter categories"
              className={clsx(
                'bg-transparent focus:outline-none text-sm',
                theme === 'dark' ? 'text-white placeholder:text-gray-500' : 'text-gray-800 placeholder:text-gray-500'
              )}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentView('epg')}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border',
                theme === 'dark'
                  ? 'bg-dark-800 border-dark-700 text-gray-200 hover:bg-dark-700'
                  : 'bg-white border-gray-200 text-gray-800 hover:bg-gray-100'
              )}
            >
              <CalendarRange size={16} />
              Guide
            </button>
            <button
              onClick={() => setCurrentView('favorites')}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border',
                theme === 'dark'
                  ? 'bg-dark-800 border-dark-700 text-gray-200 hover:bg-dark-700'
                  : 'bg-white border-gray-200 text-gray-800 hover:bg-gray-100'
              )}
            >
              <Star size={16} />
              Favorites
            </button>
          </div>
        </div>
      </div>

      <div className="relative flex items-center">
        {/* Left Scroll Button */}
        <button
          onClick={() => scroll('left')}
          className={clsx(
            'absolute left-0 z-10 p-2 rounded-r-lg transition-opacity shadow-lg',
            theme === 'dark'
              ? 'bg-dark-800 hover:bg-dark-700 text-gray-300'
              : 'bg-white hover:bg-gray-100 text-gray-700'
          )}
        >
          <ChevronLeft size={20} />
        </button>

        {/* Horizontal Scrolling Container */}
        <div
          ref={scrollContainerRef}
          className="flex items-center gap-2 px-12 pb-3 overflow-x-auto scrollbar-hide scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {/* All Categories Button */}
          <button
            onClick={() => setSelectedCategory(null)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap font-medium text-sm flex-shrink-0 border',
              !selectedCategory
                ? 'bg-primary-600 text-white shadow-lg scale-105 border-primary-500'
                : theme === 'dark'
                ? 'bg-dark-800 text-gray-200 hover:bg-dark-700 hover:text-white hover:scale-105 border-dark-700'
                : 'bg-white text-gray-800 hover:bg-gray-100 hover:text-gray-900 hover:scale-105 border-gray-200'
            )}
          >
            <Tv size={16} />
            <span>All</span>
            <span className={clsx(
              'text-xs px-2 py-1 rounded-full',
              theme === 'dark' ? 'bg-dark-700 text-gray-300' : 'bg-gray-200 text-gray-700'
            )}>{streamsForView.length}</span>
          </button>

          {/* Category Buttons */}
          {filteredCategories.map((category) => {
            const isActive = selectedCategory === category.category_id;
            const count = categoryCounts[category.category_id] || 0;

            return (
              <button
                key={category.category_id}
                onClick={() => setSelectedCategory(category.category_id)}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap font-medium text-sm flex-shrink-0 border',
                  isActive
                    ? 'bg-primary-600 text-white shadow-lg scale-105 border-primary-500'
                    : theme === 'dark'
                    ? 'bg-dark-800 text-gray-200 hover:bg-dark-700 hover:text-white hover:scale-105 border-dark-700'
                    : 'bg-white text-gray-800 hover:bg-gray-100 hover:text-gray-900 hover:scale-105 border-gray-200'
                )}
              >
                <Hash size={16} />
                <span className="truncate max-w-[140px]">{category.category_name}</span>
                <span className={clsx(
                  'text-xs px-2 py-1 rounded-full',
                  isActive
                    ? 'bg-white/20 text-white'
                    : theme === 'dark'
                    ? 'bg-dark-700 text-gray-300'
                    : 'bg-gray-200 text-gray-700'
                )}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Right Scroll Button */}
        <button
          onClick={() => scroll('right')}
          className={clsx(
            'absolute right-0 z-10 p-2 rounded-l-lg transition-opacity shadow-lg',
            theme === 'dark'
              ? 'bg-dark-800 hover:bg-dark-700 text-gray-300'
              : 'bg-white hover:bg-gray-100 text-gray-700'
          )}
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Category Count */}
      <div className={clsx(
        'px-6 py-1 text-xs border-t',
        theme === 'dark'
          ? 'text-gray-500 border-dark-700'
          : 'text-gray-500 border-gray-200'
      )}>
        {selectedCategory
          ? `${categories.find(c => c.category_id === selectedCategory)?.category_name || 'Category'} selected`
          : `${categories.length} categories available`
        }
      </div>

      {/* Custom CSS to hide scrollbar */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};
